/**
 * CrmLeadDetailModal — the guided lead detail, structured as one linear question: is there a real
 * opportunity here? Phases in order: Screening (judgment axes — is this even a prospective
 * client, how do we pace it) → Qualification (the conversation + the five questions) → Resolve
 * (convert, or the unified CloseOutBox: reason → internal note → dispositions). The Next-Step
 * card (rule engine) always leads with the active phase's real action; the stage track is
 * derived from the engine, not the raw status field. Also: AI prospect research and a rich
 * interaction timeline (type icons + inline edit/delete + prefilled logging). Edits the lead.
 *
 * Pass `lead` (normalized) to view/edit; `onSaved(lead)` fires on any change so the list refreshes.
 */
import { useEffect, useRef, useState } from 'react'
import CrmModal, { F } from './CrmModal.jsx'
import CrmLogInteractionModal from './CrmLogInteractionModal.jsx'
import CrmResearchPanel from './CrmResearchPanel.jsx'
import SlackThreadPanel from '../SlackThreadPanel.jsx'
import CrmFitSlider from './CrmFitSlider.jsx'
import { leadNextStep, URGENCY } from './crmLeadNextStep.js'
import { updateLead, qualifyLead, disqualifyLeadSafe, getLeadDetail, LEAD_SOURCES, SALUTATIONS, CLOSE_REASONS, closeReasonByKey } from '../../services/leads.js'
import { getInteractionsForLead, logInteraction, updateInteraction, deleteInteraction } from '../../services/interactions.js'
import { createContact, searchContacts } from '../../services/contacts.js'
import {
  getLeadJudgment, upsertLeadJudgment, FIT_RATING_OPTS, fitRatingColor,
  NOT_CLIENT_TYPES, NOT_CLIENT_LABELS,
} from '../../services/leadJudgment.js'
import {
  getLeadQualification, upsertLeadQualification, QUALIFICATION_QUESTIONS,
  ANSWER_OPTS, ANSWER_LABELS, CALL_GUIDE,
} from '../../services/leadQualification.js'
import { confirmDialog, alertDialog } from '../Dialog.jsx'

// Displayed stage track — DERIVED from the engine's step (plus status for the terminal states),
// not from the raw Lead Status field. Nothing in the app ever set 'Contacted'/'Engaged' before
// 2026-08 (the old track's middle stages were manual-only theater); status now auto-advances on
// logged interactions (see onLogged) so the list pills stay honest, but the track itself always
// reflects what the engine actually thinks the lead's phase is.
const STAGES = ['New', 'In conversation', 'Decision', 'Qualified']
const STEP_STAGE = {
  acknowledge: 0,
  'qualify-call': 1, momentum: 1, overdue: 1, 'slow-burn': 1, 'slow-burn-stale': 1,
  decision: 2, 'not-a-client': 2,
  ready: 3,
}
const TYPE_ICON = { 'Phone Call': '📞', 'Video Conference': '🎥', 'Video Call': '🎥', 'Email': '✉️', 'Text Message': '💬', 'In Person': '🤝', 'Meeting': '🤝', 'Action Taken': '⚡', '🎬 Action Taken': '⚡' }

// Interaction types that reflect a genuine two-way exchange — these advance the lead to Engaged.
const CONVERSATIONAL_TYPES = ['Phone Call', 'Video Conference', 'Video Call', 'In Person']
// Logged rows that aren't outreach to the lead at all — never auto-advance status off these.
const NO_ADVANCE_ACTIVITY = ['Inbound Inquiry', 'Referral Received', 'Converted to Prospect', 'Internal Update']

// Default Contact Type when a close-out adds the person to the Contacts directory, keyed by the
// judgment layer's not_a_client_type. All values are existing Contact Type options.
const NOT_CLIENT_CONTACT_TYPE = { trade_or_referral_partner: 'Referral', vendor_or_consultant: 'Vendor', other: 'Personal' }
const CONTACT_TYPE_CHOICES = ['Referral', 'General Contractor', 'Vendor', 'External Consultant', 'Personal']

const DISPOSITION_LABELS = {
  decline_message: 'Decline message copied to send',
  add_to_contacts: 'Added to Contacts directory',
  already_in_contacts: 'Already in Contacts directory',
  thanked_referrer: 'Thanked the referrer',
}

function fmtDate(v) {
  if (!v) return ''
  try { const d = new Date(v); return isNaN(d) ? String(v) : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) } catch { return String(v) }
}
function fmtDateTimeLocal(v) {
  try { const d = v ? new Date(v) : new Date(); const p = n => String(n).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}` } catch { return '' }
}
function stripMd(s) { return String(s || '').replace(/[*_#>`]/g, '').trim() }

export default function CrmLeadDetailModal({ lead, onClose, onSaved }) {
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState(() => pickForm(lead))
  const [busy, setBusy] = useState(false)
  const [interactions, setInteractions] = useState(null)   // null = loading
  const [log, setLog] = useState(null)                     // { prefill } when open
  const [closeOut, setCloseOut] = useState(null)           // null | { suggestReason?: string }
  const closeOutRef = useRef(null)
  // CloseOutBox renders far down the modal (after the field grid, Screening, Qualification, and
  // Research panel) — without this, clicking Close out only grows the scrollable content and
  // the form appears off-screen with no visible feedback that anything happened.
  useEffect(() => {
    if (closeOut && closeOutRef.current) closeOutRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [closeOut])
  const [judgment, setJudgment] = useState(undefined)       // undefined = loading; null = no row yet (common case)
  const [qualification, setQualification] = useState(undefined)  // undefined = loading; null = no row yet
  const [showSlack, setShowSlack] = useState(false)
  // Pre-conversion leads have no Project/Slack channel of their own — SlackThreadPanel resolves
  // entityType:'lead' to the fixed #aj_biz-dev channel server-side (api/slack.js). An external
  // automation already posts here on lead creation and writes the ts back to this same Airtable
  // field, so an existing thread from that automation shows up here too, not just app-started ones.
  const [localThreadTs, setLocalThreadTs] = useState(lead.slackThreadTs || '')
  // Judgment/Qualification controls have no explicit Save button — this brief cue is the only
  // feedback that a click/toggle actually persisted, rather than just changing on screen.
  const [saveFlash, setSaveFlash] = useState('')   // '' | 'judgment' | 'qualification'
  const saveFlashTimerRef = useRef(null)
  function flashSaved(which) {
    setSaveFlash(which)
    if (saveFlashTimerRef.current) clearTimeout(saveFlashTimerRef.current)
    saveFlashTimerRef.current = setTimeout(() => setSaveFlash(''), 1500)
  }
  const loadedRef = useRef(false)

  useEffect(() => {
    if (loadedRef.current || !lead) return
    loadedRef.current = true
    getInteractionsForLead(lead.id).then(setInteractions).catch(() => setInteractions([]))
    getLeadJudgment(lead.id).then(setJudgment).catch(() => setJudgment(null))
    getLeadQualification(lead.id).then(setQualification).catch(() => setQualification(null))
  }, [lead])

  // Optimistic judgment-field save — set locally first, revert + alert on failure. Every
  // Judgment-section control funnels through this. Also flashes a brief "Saved" cue (these
  // controls have no explicit Save button, so without it there's no feedback a write actually
  // took) and notifies the parent view via onSaved — judgment/qualification live in their own
  // Supabase rows, not on `lead` itself, so callers that recompute urgency off of them (e.g. the
  // Today tab's overdue/next-step badges) would otherwise stay stale until the whole page reloads.
  async function saveJudgment(patch) {
    const prev = judgment
    setJudgment(j => ({ ...(j || {}), ...patch }))
    try {
      const saved = await upsertLeadJudgment(lead.id, patch)
      if (saved) setJudgment(saved)
      flashSaved('judgment')
      onSaved && onSaved(lead)
    } catch (e) {
      setJudgment(prev)
      await alertDialog({ title: 'Save failed', message: (e && e.message) || 'Could not save.' })
    }
  }

  // Optimistic qualification-checklist save — same pattern as saveJudgment. This is what
  // crmLeadNextStep.js's `conversation_confirmed` override reads, so a human confirming the
  // qualifying conversation actually happened can override the interaction-type heuristic.
  async function saveQualification(patch) {
    const prev = qualification
    setQualification(q => ({ ...(q || {}), ...patch }))
    try {
      const saved = await upsertLeadQualification(lead.id, patch)
      if (saved) setQualification(saved)
      flashSaved('qualification')
      onSaved && onSaved(lead)
    } catch (e) {
      setQualification(prev)
      await alertDialog({ title: 'Save failed', message: (e && e.message) || 'Could not save.' })
    }
  }

  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }

  async function save() {
    if (busy) return
    setBusy(true)
    try { const s = await updateLead(lead.id, form); if (s && onSaved) onSaved(s); setEditing(false) }
    catch (e) { await alertDialog({ title: 'Save failed', message: (e && e.message) || 'Could not save the lead.' }) }
    finally { setBusy(false) }
  }
  // Soft gate, never a hard block: an incomplete checklist warns but an explicit "qualify
  // anyway" always works — the checklist guides the linear path without removing judgment calls.
  async function doQualify() {
    const q = qualification || {}
    const answered = QUALIFICATION_QUESTIONS.filter(x => q[x.key]).length
    const gaps = []
    if (!q.conversation_confirmed) gaps.push("the qualifying conversation hasn't been confirmed")
    if (answered < QUALIFICATION_QUESTIONS.length) gaps.push(`${QUALIFICATION_QUESTIONS.length - answered} of ${QUALIFICATION_QUESTIONS.length} checklist questions are unanswered`)
    if (gaps.length) {
      const ok = await confirmDialog({
        title: 'Qualify anyway?',
        message: `The qualification checklist isn't complete — ${gaps.join(', and ')}. Qualify anyway?`,
        confirmLabel: 'Qualify anyway',
      })
      if (!ok) return
    }
    setBusy(true)
    try { const s = await qualifyLead(lead.id); if (s && onSaved) onSaved(s) }
    catch (e) { await alertDialog({ title: 'Qualify failed', message: (e && e.message) || 'Could not qualify the lead.' }) }
    finally { setBusy(false) }
  }
  async function doRequalify() {
    setBusy(true)
    try {
      const s = await updateLead(lead.id, { status: 'Contacted' }); if (s && onSaved) onSaved(s)
      // Axis 5 (reactivation): only a REAL reactivation (was actually Disqualified), not a
      // no-op re-save — flags that pre-disqualification interactions shouldn't count toward
      // qualification-conversation gates until a fresh one is logged.
      if (lead.status === 'Disqualified') {
        upsertLeadJudgment(lead.id, { reactivated_at: new Date().toISOString(), needs_fresh_evaluation: true })
          .then(setJudgment).catch(() => {})
      }
    }
    catch (e) { await alertDialog({ title: 'Requalify failed', message: (e && e.message) || 'Could not requalify the lead.' }) }
    finally { setBusy(false) }
  }

  // Unified close-out — the ONE way a lead is disqualified. Writes the nearest Airtable
  // Disqualification Reason (with 'Other' fallback if the precise option can't be created),
  // stores the exact reason + internal note + dispositions on the lead_judgment row
  // (database/147_lead_closeout.sql), runs the chosen dispositions, and logs one interaction
  // summarizing all of it.
  async function doCloseOut({ reasonKey, note, dispositions, contactType }) {
    const reason = closeReasonByKey(reasonKey) || closeReasonByKey('other')
    setBusy(true)
    try {
      const s = await disqualifyLeadSafe(lead.id, reason.airtable, reason.fallback)
      if (s && onSaved) onSaved(s)

      // Disposition: add to Contacts directory (best-effort, deduped by email when we have one).
      const dispo = [...dispositions]
      if (dispo.indexOf('add_to_contacts') !== -1) {
        try {
          let exists = false
          if (lead.email) {
            const found = await searchContacts(lead.email).catch(() => null)
            exists = !!(found && (found.contacts || []).some(c => String((c.fields || {})['Email'] || '').toLowerCase() === lead.email.toLowerCase()))
          }
          if (exists) {
            dispo[dispo.indexOf('add_to_contacts')] = 'already_in_contacts'
          } else {
            const fields = { 'First Name': lead.firstName || lead.name, 'Contact Type': contactType || 'Referral' }
            if (lead.lastName) fields['Last Name'] = lead.lastName
            if (lead.email) fields['Email'] = lead.email
            if (lead.phone) fields['Phone'] = lead.phone
            await createContact(fields, 'lead')
          }
        } catch (_) { /* best-effort — the close-out itself already succeeded */ }
      }

      const dispoSummary = dispo.map(d => DISPOSITION_LABELS[d] || d).join('; ')
      const summary = [`Closed out — ${reason.label}.`, note || '', dispoSummary].filter(Boolean).join(' — ')
      const it = await logInteraction({
        leadId: lead.id,
        type: dispo.indexOf('decline_message') !== -1 ? 'Email' : 'Action Taken',
        leadActivityType: 'Lead Disengaged (Reason Provided)', notes: summary,
      })
      if (it) setInteractions(list => [it, ...(list || [])])
      upsertLeadJudgment(lead.id, {
        last_disqualified_at: new Date().toISOString(),
        close_reason: reasonKey, close_note: note || null, close_dispositions: dispo,
      }).then(setJudgment).catch(() => {})
      setCloseOut(null)
      refreshLead()   // pick up the rollups the just-logged interaction changed
    } catch (e) { await alertDialog({ title: 'Close out failed', message: (e && e.message) || 'Could not close out the lead.' }) }
    finally { setBusy(false) }
  }

  // Escape hatch out of the close-out: a "timeline never materialized"/"went cold" lead that
  // isn't really dead becomes a slow burn (judgment axis 2) instead of being disqualified.
  function keepAsSlowBurn() {
    setCloseOut(null)
    saveJudgment({ slow_burn: true })
  }

  // The list views' `daysSinceContact`/`latestInteraction`/`rating` are Airtable rollup/formula
  // fields computed off the linked Interactions — no amount of local state patches surfaces the
  // recalculated value, and CrmLeadsView's onSaved does an in-place MERGE (not a refetch), so
  // passing the stale `lead` prop would just re-merge the same old numbers. Re-fetch the record
  // and hand THAT to onSaved so every interaction add/edit/delete actually refreshes what the
  // list is showing, not just this modal's own local timeline.
  async function refreshLead() {
    try { const fresh = await getLeadDetail(lead.id); if (fresh && onSaved) onSaved(fresh) } catch (_) { /* list stays as-is; not worth surfacing */ }
  }

  async function onLogged(it) {
    if (!it) return
    setInteractions(list => [it, ...(list || [])])
    // Axis 5: the first fresh interaction after reactivation clears the flag — from then on
    // this lead evaluates on its own new merits, same as any other active lead.
    if (judgment && judgment.needs_fresh_evaluation) {
      upsertLeadJudgment(lead.id, { needs_fresh_evaluation: false }).then(setJudgment).catch(() => {})
    }
    // Status auto-advance — before 2026-08 nothing ever moved a lead to Contacted/Engaged, so
    // the list's status pills only changed when someone edited the raw field by hand. A logged
    // outreach advances New → Contacted; a genuine two-way exchange advances to Engaged.
    // Internal notes and auto-logged source rows never advance anything. Awaited before
    // refreshLead so the refetch can't race the PATCH and hand the list a stale status.
    try {
      const noAdvance = NO_ADVANCE_ACTIVITY.indexOf(it.leadActivityType) !== -1
      if (!noAdvance && !lead.converted && (lead.status === 'New' || lead.status === 'Contacted')) {
        const conversational = it.leadActivityType === 'Engaged with Lead' || CONVERSATIONAL_TYPES.indexOf(it.type) !== -1
        if (conversational) await updateLead(lead.id, { status: 'Engaged' })
        else if (lead.status === 'New') await updateLead(lead.id, { status: 'Contacted' })
      }
    } catch (_) { /* best-effort — the interaction itself already logged */ }
    refreshLead()
  }
  function beginConvert() { window.dispatchEvent(new CustomEvent('open-convert-lead', { detail: { leadId: lead.id } })) }

  async function removeInteraction(it) {
    if (!(await confirmDialog({ title: 'Delete interaction?', message: 'This removes the logged interaction permanently.', confirmLabel: 'Delete', danger: true }))) return
    setInteractions(list => (list || []).filter(x => x.id !== it.id))
    try { await deleteInteraction(it.id) } catch (e) { /* re-sync on next load */ }
    refreshLead()
  }
  function onEdited(updated) {
    if (updated) setInteractions(list => (list || []).map(x => x.id === updated.id ? updated : x))
    refreshLead()
  }

  // Don't compute the card until interactions, judgment, AND the qualification checklist have
  // all loaded — otherwise it flashes an intermediate state even when overrides already exist.
  const stepDataLoaded = interactions !== null && judgment !== undefined && qualification !== undefined
  const step = (lead && stepDataLoaded) ? leadNextStep(lead, interactions || [], judgment, qualification) : null

  const footer = editing ? (
    <>
      <button style={F.btnGhost} onClick={() => { setForm(pickForm(lead)); setEditing(false) }} disabled={busy}>Cancel</button>
      <button style={F.btnPrimary} onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
    </>
  ) : (
    <button style={F.btnGhost} onClick={() => setEditing(true)} disabled={busy}>Edit lead</button>
  )

  return (
    <>
      <CrmModal title={lead.name || 'Lead'} onClose={onClose} footer={footer} width={640}>
        {editing ? <LeadForm form={form} set={set} /> : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
              <div style={{ flex: 1 }}><StageTrack status={lead.status} converted={lead.converted} step={step} disqualReason={lead.disqualReason} /></div>
              <button
                onClick={() => setShowSlack(true)}
                title={localThreadTs ? 'Open team discussion (Slack)' : 'Start a team discussion (Slack)'}
                style={{
                  background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 5,
                  padding: '6px 8px', cursor: 'pointer', color: 'var(--aj-text-mid)',
                  display: 'flex', alignItems: 'center', flexShrink: 0,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </button>
            </div>

            {step && <NextStepCard step={step} busy={busy}
              onLog={() => setLog({ prefill: step.prefill })}
              onQualify={doQualify} onCloseOut={() => setCloseOut({ suggestReason: step.suggestReason })}
              onConvert={beginConvert} onRequalify={doRequalify} />}

            {step && step.step === 'qualify-call' && <CallGuide />}

            <div style={S.fieldGrid}>
              <Field label="Organization" value={lead.organization} />
              <Field label="Source" value={[lead.source, lead.inboundOption || lead.existingDbOption].filter(Boolean).join(' · ')} />
              <Field label="Email" value={lead.email} mono />
              <Field label="Phone" value={lead.phone} mono />
              <Field label="Location" value={[lead.city, lead.state].filter(Boolean).join(', ')} />
              <Field label="Last interaction" value={fmtDate(lead.latestInteraction)} mono />
            </div>
            {lead.description && (
              <div style={{ marginTop: 4, marginBottom: 6 }}>
                <div style={F.label}>Notes</div>
                <div style={S.notes}>{lead.description}</div>
              </div>
            )}

            {/* Phase 1 — Screening (the judgment axes): is this even a prospective client, and
                how should we pace it? Open while the lead is early; a summary line after. */}
            <Section num="1" label="Screening" flash={saveFlash === 'judgment'}
              summary={screeningSummary(judgment)}
              defaultOpen={!!step && (step.step === 'acknowledge' || step.step === 'not-a-client')}>
              {judgment !== undefined && <JudgmentSection judgment={judgment || {}} onSave={saveJudgment} />}
            </Section>

            {/* Phase 2 — Qualification: the conversation + the five questions that decide it.
                Open while the qualify call / decision is the active phase. */}
            <Section num="2" label="Qualification" flash={saveFlash === 'qualification'}
              summary={qualificationSummary(qualification)}
              defaultOpen={!!step && (step.step === 'qualify-call' || step.step === 'decision')}>
              {qualification !== undefined && <QualificationSection qualification={qualification || {}} onSave={saveQualification} />}
            </Section>

            <CrmResearchPanel lead={lead} />

            {closeOut && <div ref={closeOutRef}><CloseOutBox lead={lead} judgment={judgment} busy={busy}
              suggestReason={closeOut.suggestReason}
              onConfirm={doCloseOut} onSlowBurn={keepAsSlowBurn} onCancel={() => setCloseOut(null)} /></div>}

            <div style={S.histHeadRow}>
              <span style={S.histHead}>Interactions</span>
              <button style={{ ...F.btnGhost, padding: '3px 10px', fontSize: 11.5 }} onClick={() => setLog({ prefill: null })} disabled={busy}>+ Log interaction</button>
            </div>
            <Timeline interactions={interactions} onEdited={onEdited} onDelete={removeInteraction} />
          </>
        )}
      </CrmModal>

      {log && (
        <CrmLogInteractionModal leadId={lead.id} prefill={log.prefill} onClose={() => setLog(null)} onLogged={onLogged} />
      )}

      {showSlack && (
        <SlackThreadPanel
          entityId={lead.id}
          entityType="lead"
          entityName={lead.name || 'Lead'}
          threadTs={localThreadTs}
          onClose={() => setShowSlack(false)}
          onThreadStarted={ts => setLocalThreadTs(ts)}
        />
      )}
    </>
  )
}

/* ── Stage track — derived from the engine's step, not the raw status field ─ */
function StageTrack({ status, converted, step, disqualReason }) {
  if (status === 'Disqualified') {
    return <div style={{ ...S.track, color: '#b3261e' }}><span style={S.trackX}>✕</span><span style={S.trackLbl}>Closed out{disqualReason ? ` · ${disqualReason}` : ''}</span></div>
  }
  const fromStatus = status === 'Qualified' ? 3 : status === 'Engaged' ? 2 : status === 'Contacted' ? 1 : 0
  const activeIdx = converted ? STAGES.length
    : (step && STEP_STAGE[step.step] !== undefined) ? STEP_STAGE[step.step]
    : fromStatus
  return (
    <div style={S.track}>
      {STAGES.map((s, i) => {
        const done = converted || i < activeIdx
        const active = !converted && i === activeIdx
        return (
          <span key={s} style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ ...S.tdot, ...(done ? S.tdotDone : active ? S.tdotActive : {}) }}>{done ? '✓' : ''}</span>
            <span style={{ ...S.tlbl, ...(active ? { color: 'var(--aj-text)', fontWeight: 600 } : done ? { color: 'var(--aj-text-mid)' } : {}) }}>{s}</span>
            {i < STAGES.length - 1 && <span style={S.tline} />}
          </span>
        )
      })}
      {converted && <span style={{ display: 'flex', alignItems: 'center' }}><span style={S.tline} /><span style={{ ...S.tdot, ...S.tdotDone }}>✓</span><span style={{ ...S.tlbl, color: '#1f9d57', fontWeight: 600 }}>Converted</span></span>}
    </div>
  )
}

/* ── Next-step card — the primary button is always the step's actual next move ─────────────
   (a CLOSED-looking card whose primary action was "Log interaction" was the old Beth Gale
   confusion; a decision step now leads with whatever the checklist recommends). ──────────── */
function NextStepCard({ step, busy, onLog, onQualify, onCloseOut, onConvert, onRequalify }) {
  const u = URGENCY[step.urgency] || URGENCY.amber
  let buttons = null
  if (step.action === 'decision') {
    buttons = step.recommend === 'qualify' ? (
      <>
        <button style={F.btnPrimary} onClick={onQualify} disabled={busy}>Qualify</button>
        <button style={F.btnGhost} onClick={onLog} disabled={busy}>Log interaction</button>
        <button style={F.btnGhost} onClick={onCloseOut} disabled={busy}>Close out</button>
      </>
    ) : step.recommend === 'close' ? (
      <>
        <button style={F.btnPrimary} onClick={onCloseOut} disabled={busy}>Close out lead</button>
        <button style={F.btnGhost} onClick={onQualify} disabled={busy}>Qualify</button>
        <button style={F.btnGhost} onClick={onLog} disabled={busy}>Log interaction</button>
      </>
    ) : (
      <>
        <button style={F.btnPrimary} onClick={onLog} disabled={busy}>Log interaction</button>
        <button style={F.btnGhost} onClick={onQualify} disabled={busy}>Qualify</button>
        <button style={F.btnGhost} onClick={onCloseOut} disabled={busy}>Close out</button>
      </>
    )
  } else if (step.action === 'closeout') {
    buttons = (
      <>
        <button style={F.btnPrimary} onClick={onCloseOut} disabled={busy}>Close out lead</button>
        <button style={F.btnGhost} onClick={onLog} disabled={busy}>Log interaction</button>
      </>
    )
  } else if (step.action === 'convert') {
    buttons = (
      <>
        <button style={F.btnPrimary} onClick={onConvert} disabled={busy}>Begin conversion</button>
        <button style={F.btnGhost} onClick={onCloseOut} disabled={busy}>Close out</button>
      </>
    )
  } else if (step.action === 'requalify') {
    buttons = <button style={F.btnPrimary} onClick={onRequalify} disabled={busy}>Requalify</button>
  } else if (step.action === 'log') {
    buttons = (
      <>
        <button style={F.btnPrimary} onClick={onLog} disabled={busy}>Log interaction</button>
        <button style={F.btnGhost} onClick={onCloseOut} disabled={busy}>Close out</button>
      </>
    )
  }
  return (
    <div style={{ ...S.card, background: u.bg, borderColor: u.border }}>
      <div style={{ ...S.cardBadge, color: u.accent }}>{u.label}</div>
      <div style={S.cardTitle}>{step.title}</div>
      <div style={S.cardDesc}>{step.desc}</div>
      <div style={S.cardActions}>{buttons}</div>
    </div>
  )
}

/* ── Collapsible phase section — open while it's the active phase, a summary line after. A
   user toggle always wins over the phase default once clicked. ─────────────────────────── */
function Section({ num, label, summary, defaultOpen, flash, children }) {
  const [userOpen, setUserOpen] = useState(null)
  const open = userOpen === null ? !!defaultOpen : userOpen
  return (
    <div>
      <div style={{ ...S.sectionRow, cursor: 'pointer' }} onClick={() => setUserOpen(!open)}>
        <span style={S.sectionNum}>{num}</span>
        <span style={S.sectionLbl}>{label}</span>
        {flash && <span style={S.savedFlash}>Saved ✓</span>}
        {!open && summary && <span style={S.sectionSummary}>{summary}</span>}
        <span style={{ flex: 1 }} />
        <span style={S.sectionToggle}>{open ? 'Hide' : 'Show'}</span>
      </div>
      {open && children}
    </div>
  )
}

function screeningSummary(judgment) {
  if (judgment === undefined) return ''
  const j = judgment || {}
  const bits = []
  if (j.not_a_client_type) bits.push(`Not a client — ${NOT_CLIENT_LABELS[j.not_a_client_type] || j.not_a_client_type}`)
  if (j.relationship_sensitive) bits.push('Relationship-sensitive')
  if (j.slow_burn) bits.push('Slow burn')
  if (j.fit_rating) bits.push(j.fit_rating)
  return bits.length ? bits.join(' · ') : 'No flags'
}

function qualificationSummary(qualification) {
  if (qualification === undefined) return ''
  const q = qualification || {}
  const answered = QUALIFICATION_QUESTIONS.filter(x => q[x.key]).length
  const bits = [`${answered}/${QUALIFICATION_QUESTIONS.length} answered`]
  if (q.conversation_confirmed) bits.push('conversation confirmed')
  return bits.join(' · ')
}

/* ── Qualification checklist — the four questions that decide qualify/disqualify, made ──────
   explicit and recorded instead of living entirely in the reviewer's head. `conversation_
   confirmed` is what crmLeadNextStep.js's hasQualConvo reads as a human override — only a
   person can judge whether the qualifying conversation was actually substantive. ──────────── */
function QualificationSection({ qualification, onSave }) {
  const [notes, setNotes] = useState(qualification.notes || '')

  return (
    <div style={{ marginBottom: 6 }}>
      <label style={S.judgRow}>
        <input type="checkbox" checked={!!qualification.conversation_confirmed}
          onChange={e => onSave({ conversation_confirmed: e.target.checked })} />
        <span style={S.judgLbl}>The qualifying conversation has actually happened — with the real decision-maker, not an intermediary</span>
      </label>

      {QUALIFICATION_QUESTIONS.map(q => (
        <div key={q.key} style={S.qualQRow}>
          <span style={S.qualQLbl}>{q.label}</span>
          <div style={{ display: 'flex', gap: 4 }}>
            {ANSWER_OPTS.map(opt => {
              const active = qualification[q.key] === opt
              return (
                <button key={opt} type="button"
                  style={{ ...S.qualAnsBtn, ...(active ? S.qualAnsBtnActive : {}) }}
                  onClick={() => onSave({ [q.key]: active ? null : opt })}>
                  {ANSWER_LABELS[opt]}
                </button>
              )
            })}
          </div>
        </div>
      ))}

      <div style={{ marginTop: 8 }}>
        <textarea style={{ ...F.textarea, minHeight: 48 }} placeholder="Notes from the qualifying conversation…"
          value={notes} onChange={e => setNotes(e.target.value)} onBlur={() => onSave({ notes })} />
      </div>
    </div>
  )
}

/* ── Call guide — internal reference for the qualifying conversation itself, shown only while ─
   that call is the recommended next step. Never a script to read verbatim to the prospect. ── */
function CallGuide() {
  const [open, setOpen] = useState(true)
  return (
    <div style={S.guideBox}>
      <div style={S.guideHeadRow} onClick={() => setOpen(v => !v)}>
        <span style={S.guideHead}>{CALL_GUIDE.title}</span>
        <span style={S.guideToggle}>{open ? 'Hide' : 'Show'}</span>
      </div>
      {open && (
        <>
          {CALL_GUIDE.items.map(item => (
            <div key={item.key} style={{ marginBottom: 8 }}>
              <div style={S.guideQ}>{item.q}</div>
              <div style={S.guideTip}>{item.tip}</div>
            </div>
          ))}
          <div style={S.guideClosing}>{CALL_GUIDE.closing}</div>
        </>
      )}
    </div>
  )
}

/* ── Judgment layer — the 5-axis overrides on top of the generic urgency engine ─────────── */
function JudgmentSection({ judgment, onSave }) {
  const [note, setNote] = useState(judgment.relationship_note || '')
  const [checkinOn, setCheckinOn] = useState(judgment.slow_burn_next_checkin_on || '')
  const [notClientNote, setNotClientNote] = useState(judgment.not_a_client_note || '')

  return (
    <div style={{ marginBottom: 6 }}>
      <label style={S.judgRow}>
        <input type="checkbox" checked={!!judgment.relationship_sensitive}
          onChange={e => onSave({ relationship_sensitive: e.target.checked })} />
        <span style={S.judgLbl}>Relationship-sensitive — pace this with a personal/social cadence, not the standard clock</span>
      </label>
      {judgment.relationship_sensitive && (
        <div style={{ marginLeft: 22, marginBottom: 12 }}>
          <textarea style={{ ...F.textarea, minHeight: 48 }} placeholder="Why (e.g. existing relationship with the firm)…"
            value={note} onChange={e => setNote(e.target.value)} onBlur={() => onSave({ relationship_note: note })} />
        </div>
      )}

      <label style={S.judgRow}>
        <input type="checkbox" checked={!!judgment.slow_burn}
          onChange={e => onSave({ slow_burn: e.target.checked })} />
        <span style={S.judgLbl}>Slow burn — no active timeline, runs its own long-horizon cadence</span>
      </label>
      {judgment.slow_burn && (
        <div style={{ marginLeft: 22, marginBottom: 12 }}>
          <label style={F.label}>Next low-pressure check-in</label>
          <input style={{ ...F.input, width: 180 }} type="date" value={checkinOn}
            onChange={e => setCheckinOn(e.target.value)} onBlur={() => onSave({ slow_burn_next_checkin_on: checkinOn || null })} />
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <CrmFitSlider label="Fit rating" options={FIT_RATING_OPTS} colorFn={fitRatingColor}
          value={judgment.fit_rating} onChange={v => onSave({ fit_rating: v })} />
      </div>

      <div style={F.row}>
        <label style={F.label}>Not a prospective client</label>
        <select style={F.input} value={judgment.not_a_client_type || ''}
          onChange={e => onSave({ not_a_client_type: e.target.value || null })}>
          <option value="">— This is a client lead —</option>
          {NOT_CLIENT_TYPES.map(t => <option key={t} value={t}>{NOT_CLIENT_LABELS[t]}</option>)}
        </select>
      </div>
      {judgment.not_a_client_type && (
        <div style={{ marginBottom: 4 }}>
          <textarea style={{ ...F.textarea, minHeight: 48 }} placeholder="Context (optional)…"
            value={notClientNote} onChange={e => setNotClientNote(e.target.value)}
            onBlur={() => onSave({ not_a_client_note: notClientNote })} />
        </div>
      )}
    </div>
  )
}

/* ── Close out — the unified resolve flow: reason → internal note → dispositions ───────────
   ("determine if anything further needs to be done before we disengage" made explicit).
   Replaces the old two-path DeclineBox, whose only exits (went cold / declining) didn't fit
   partner/vendor closes at all. ──────────────────────────────────────────────────────────── */
function CloseOutBox({ lead, judgment, busy, suggestReason, onConfirm, onSlowBurn, onCancel }) {
  const [reasonKey, setReasonKey] = useState(suggestReason || '')
  const [note, setNote] = useState('')
  const [sendDecline, setSendDecline] = useState(false)
  const [addToContacts, setAddToContacts] = useState(false)
  const [thankedReferrer, setThankedReferrer] = useState(false)
  const [contactType, setContactType] = useState(
    NOT_CLIENT_CONTACT_TYPE[(judgment && judgment.not_a_client_type) || ''] || 'Referral')
  const [copied, setCopied] = useState('')
  const reason = closeReasonByKey(reasonKey)

  const subject = 'Re: Your inquiry with Alder James'
  // Reason-specific decline copy. Voice per brand/alder-james-brand-profile.md: the confident,
  // restrained Studio register. Never quotes fee numbers (the fee floor is an internal
  // gut-check, not a number for the prospect — see CALL_GUIDE).
  const hi = `Hi ${lead.firstName || 'there'},`
  const body = reasonKey === 'budget'
    ? `${hi}\n\nThank you for reaching out to Alder James and for sharing your project with us. We've given it careful thought.\n\nBeing honest with you early matters more to us than a longer conversation that ends in the same place: we don't believe our fee structure is the right match for the budget you have in mind. We'd rather say that clearly now, before momentum builds around a plan that doesn't serve you.\n\nWe wish you every success with the project. If your plans change down the road, we'd be glad to hear from you.\n\nWarm regards,\nAlder James`
    : `${hi}\n\nThank you for reaching out to Alder James. We've genuinely appreciated learning about your project and the opportunity to be considered.\n\nAfter careful thought, we don't feel we're the right fit for this particular project at this time. This isn't a reflection of the project itself, it's simply a matter of fit and capacity.\n\nWe wish you every success with it, and hope you find the right team. If your circumstances change or another project comes along down the road, please don't hesitate to reach out.\n\nWarm regards,\nAlder James`
  function copy(text, which) { try { navigator.clipboard.writeText(text); setCopied(which); setTimeout(() => setCopied(''), 1800) } catch (e) {} }

  function confirm() {
    const dispositions = []
    if (sendDecline && reason && reason.decline) dispositions.push('decline_message')
    if (addToContacts && reason && reason.contacts) dispositions.push('add_to_contacts')
    if (thankedReferrer && lead.referredByName) dispositions.push('thanked_referrer')
    onConfirm({ reasonKey: reasonKey || 'other', note, dispositions, contactType })
  }

  return (
    <div style={S.declineBox}>
      <div style={{ ...F.label, marginBottom: 6 }}>Close out lead</div>

      <div style={{ marginBottom: 10 }}>
        <div style={F.label}>Why are we closing this out?</div>
        <select style={F.input} value={reasonKey} onChange={e => { setReasonKey(e.target.value); setSendDecline(false); setAddToContacts(false) }} disabled={busy}>
          <option value="">— Choose a reason —</option>
          {CLOSE_REASONS.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
      </div>

      <div style={{ marginBottom: 10 }}>
        <div style={F.label}>Internal note — context for our own records (not sent to the client)</div>
        <textarea style={{ ...F.textarea, minHeight: 44 }} placeholder="e.g. who they were, what would bring this back to life…"
          value={note} onChange={e => setNote(e.target.value)} disabled={busy} />
      </div>

      {reason && (reason.decline || reason.contacts || reason.slowBurnHint || lead.referredByName) && (
        <div style={{ marginBottom: 10 }}>
          <div style={F.label}>Before we disengage</div>

          {reason.slowBurnHint && (
            <div style={S.dispoHint}>
              Not actually dead, just a long horizon? <button style={S.dispoLink} onClick={onSlowBurn} disabled={busy}>Keep as a slow burn instead</button> — no disqualification, its own low-pressure cadence.
            </div>
          )}

          {reason.decline && (
            <label style={S.judgRow}>
              <input type="checkbox" checked={sendDecline} onChange={e => setSendDecline(e.target.checked)} disabled={busy} />
              <span style={S.judgLbl}>Send the decline message — a warm, door-open close</span>
            </label>
          )}
          {reason.decline && sendDecline && (
            <div style={{ marginLeft: 22, marginBottom: 10 }}>
              <div style={{ fontFamily: '"DM Mono", monospace', fontSize: 11.5, color: 'var(--aj-text-mid)', marginBottom: 6 }}>
                To: {lead.email || '(no email on file)'} · Subject: {subject} <button style={S.copyMini} onClick={() => copy(subject, 'subj')}>{copied === 'subj' ? 'Copied' : 'copy'}</button>
              </div>
              <div style={S.declineBody}>{body}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
                <button style={F.btnGhost} onClick={() => copy(body, 'body')}>{copied === 'body' ? 'Copied' : 'Copy message'}</button>
                <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-mid)' }}>
                  Copied to your clipboard — paste it into your own email and send it yourself.
                </span>
              </div>
            </div>
          )}

          {reason.contacts && (
            <label style={S.judgRow}>
              <input type="checkbox" checked={addToContacts} onChange={e => setAddToContacts(e.target.checked)} disabled={busy} />
              <span style={S.judgLbl}>Add {lead.name || 'them'} to the Contacts directory — keep the relationship, drop the funnel</span>
            </label>
          )}
          {reason.contacts && addToContacts && (
            <div style={{ marginLeft: 22, marginBottom: 10 }}>
              <label style={F.label}>Contact type</label>
              <select style={{ ...F.input, width: 220 }} value={contactType} onChange={e => setContactType(e.target.value)} disabled={busy}>
                {CONTACT_TYPE_CHOICES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}

          {lead.referredByName && (
            <label style={S.judgRow}>
              <input type="checkbox" checked={thankedReferrer} onChange={e => setThankedReferrer(e.target.checked)} disabled={busy} />
              <span style={S.judgLbl}>Referred by {lead.referredByName} — I've thanked them (or will) so the referral channel stays warm</span>
            </label>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <div style={{ flex: 1 }} />
        <button style={{ ...F.btnGhost, border: 'none' }} onClick={onCancel} disabled={busy}>Cancel</button>
        <button style={{ ...F.btnPrimary, background: '#b3261e' }} onClick={confirm} disabled={busy || !reasonKey}>Close out lead</button>
      </div>
    </div>
  )
}

/* ── Interaction timeline ────────────────────────────────────────────────── */
function Timeline({ interactions, onEdited, onDelete }) {
  if (interactions === null) return <div style={S.muted}>Loading…</div>
  if (!interactions.length) return <div style={S.muted}>No interactions logged yet.</div>
  return <div>{interactions.map(it => <TimelineRow key={it.id} it={it} onEdited={onEdited} onDelete={onDelete} />)}</div>
}

function TimelineRow({ it, onEdited, onDelete }) {
  const [editing, setEditing] = useState(false)
  const [notes, setNotes] = useState(it.notes || '')
  const [date, setDate] = useState(fmtDateTimeLocal(it.date))
  const [busy, setBusy] = useState(false)

  async function save() {
    setBusy(true)
    try { const u = await updateInteraction(it.id, { notes, date: date ? new Date(date).toISOString() : it.date }); if (u) onEdited(u); setEditing(false) }
    catch (e) { await alertDialog({ title: 'Save failed', message: (e && e.message) || 'Could not save the interaction.' }) }
    finally { setBusy(false) }
  }

  return (
    <div style={S.histRow}>
      <div style={S.histIcon}>{TYPE_ICON[it.type] || '•'}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={S.histType}>{[it.type, it.leadActivityType].filter(Boolean).join(' · ')}</div>
        {editing ? (
          <div style={{ marginTop: 6 }}>
            <textarea style={{ ...F.textarea, minHeight: 56 }} value={notes} onChange={e => setNotes(e.target.value)} />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
              <input style={{ ...F.input, width: 200 }} type="datetime-local" value={date} onChange={e => setDate(e.target.value)} />
              <div style={{ flex: 1 }} />
              <button style={{ ...F.btnGhost, border: 'none', padding: '4px 8px' }} onClick={() => { setNotes(it.notes || ''); setEditing(false) }} disabled={busy}>Cancel</button>
              <button style={{ ...F.btnPrimary, padding: '6px 12px' }} onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        ) : (
          <>
            {it.notes && <div style={S.histNotes}>{stripMd(it.notes)}</div>}
            <div style={S.histMeta}>{fmtDate(it.date)}{it.teamMember ? ` · ${it.teamMember}` : ''}</div>
          </>
        )}
      </div>
      {!editing && (
        <div style={S.histActions}>
          <button style={S.iconBtn} title="Edit" onClick={() => setEditing(true)}>✎</button>
          <button style={S.iconBtn} title="Delete" onClick={() => onDelete(it)}>🗑</button>
        </div>
      )}
    </div>
  )
}

/* ── Edit form ───────────────────────────────────────────────────────────── */
// Status is deliberately NOT editable here — it moves only through the guided actions
// (qualify / close out / requalify / auto-advance on logged interactions). The old raw Status
// dropdown was a backdoor around the whole flow.
function pickForm(lead) {
  return {
    salutation: lead.salutation || '', firstName: lead.firstName || '', lastName: lead.lastName || '',
    organization: lead.organization || '', email: lead.email || '', phone: lead.phone || '',
    source: lead.source || '', city: lead.city || '', state: lead.state || '',
    description: lead.description || '',
  }
}
function LeadForm({ form, set }) {
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 1fr', gap: 12 }}>
        <div style={F.row}><label style={F.label}>Title</label><select style={F.input} value={form.salutation} onChange={e => set('salutation', e.target.value)}><option value="">—</option>{SALUTATIONS.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
        <div style={F.row}><label style={F.label}>First name</label><input style={F.input} value={form.firstName} onChange={e => set('firstName', e.target.value)} /></div>
        <div style={F.row}><label style={F.label}>Last name</label><input style={F.input} value={form.lastName} onChange={e => set('lastName', e.target.value)} /></div>
      </div>
      <div style={F.row}><label style={F.label}>Organization</label><input style={F.input} value={form.organization} onChange={e => set('organization', e.target.value)} /></div>
      <div style={F.grid2}>
        <div style={F.row}><label style={F.label}>Email</label><input style={F.input} type="email" value={form.email} onChange={e => set('email', e.target.value)} /></div>
        <div style={F.row}><label style={F.label}>Phone</label><input style={F.input} value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
      </div>
      <div style={F.grid2}>
        <div style={F.row}><label style={F.label}>Source</label><select style={F.input} value={form.source} onChange={e => set('source', e.target.value)}><option value="">—</option>{LEAD_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
        <div style={F.row}><label style={F.label}>City</label><input style={F.input} value={form.city} onChange={e => set('city', e.target.value)} /></div>
      </div>
      <div style={F.grid2}>
        <div style={F.row}><label style={F.label}>State</label><input style={F.input} value={form.state} onChange={e => set('state', e.target.value)} /></div>
      </div>
      <div style={F.row}><label style={F.label}>Notes</label><textarea style={F.textarea} value={form.description} onChange={e => set('description', e.target.value)} /></div>
    </>
  )
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div style={F.label}>{label}</div>
      <div style={{ fontFamily: mono ? '"DM Mono", monospace' : '"DM Sans", sans-serif', fontSize: 13, color: value ? 'var(--aj-text)' : 'var(--aj-text-mid)' }}>{value || '—'}</div>
    </div>
  )
}

const S = {
  track: { display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'wrap', marginBottom: 16 },
  tdot: { width: 16, height: 16, borderRadius: '50%', background: 'var(--aj-bg-muted)', color: '#fff', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  tdotDone: { background: '#1f9d57' },
  tdotActive: { background: 'var(--aj-text)', boxShadow: '0 0 0 3px rgba(0,0,0,0.12)' },
  tlbl: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', margin: '0 4px 0 5px' },
  tline: { width: 18, height: 1, background: 'var(--aj-border)', display: 'inline-block' },
  trackX: { width: 16, height: 16, borderRadius: '50%', background: '#b3261e', color: '#fff', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  trackLbl: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, fontWeight: 600, marginLeft: 6 },

  card: { border: '0.5px solid', borderRadius: 8, padding: '12px 14px', marginBottom: 16 },
  cardBadge: { fontFamily: '"Montserrat", sans-serif', fontSize: 9.5, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 5 },
  cardTitle: { fontFamily: '"DM Sans", sans-serif', fontSize: 14, fontWeight: 600, color: 'var(--aj-text)', marginBottom: 3 },
  cardDesc: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', lineHeight: 1.5, marginBottom: 10 },
  cardActions: { display: 'flex', gap: 8, flexWrap: 'wrap' },

  fieldGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 8 },
  notes: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text)', whiteSpace: 'pre-wrap', lineHeight: 1.45 },


  sectionRow: { display: 'flex', alignItems: 'center', margin: '14px 0 10px', borderTop: '0.5px solid var(--aj-border)', paddingTop: 14 },
  sectionLbl: { fontFamily: '"Montserrat", sans-serif', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--aj-text-mid)' },
  sectionNum: { fontFamily: '"DM Mono", monospace', fontSize: 10, color: 'var(--aj-text-muted)', border: '0.5px solid var(--aj-border)', borderRadius: '50%', width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginRight: 7, flexShrink: 0 },
  sectionSummary: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', marginLeft: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  sectionToggle: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', flexShrink: 0 },
  savedFlash: { fontFamily: '"DM Mono", monospace', fontSize: 10.5, color: '#1D9E75', marginLeft: 8 },
  dispoHint: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', lineHeight: 1.5, marginBottom: 8 },
  dispoLink: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#1a6fa8', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' },
  judgRow: { display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, cursor: 'pointer' },
  judgLbl: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text)', lineHeight: 1.4, marginTop: 1 },

  qualQRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '6px 0', borderBottom: '0.5px solid var(--aj-border)' },
  qualQLbl: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text)', lineHeight: 1.4, flex: 1 },
  qualAnsBtn: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 20, padding: '4px 10px', cursor: 'pointer', flexShrink: 0 },
  qualAnsBtnActive: { background: 'var(--aj-text)', color: 'var(--aj-bg-page)', borderColor: 'var(--aj-text)' },

  guideBox: { background: 'rgba(29,158,117,0.05)', border: '0.5px solid rgba(29,158,117,0.25)', borderRadius: 8, padding: '10px 12px', marginBottom: 16 },
  guideHeadRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' },
  guideHead: { fontFamily: '"Montserrat", sans-serif', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#1f9d57' },
  guideToggle: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-mid)' },
  guideQ: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, fontWeight: 600, color: 'var(--aj-text)', marginTop: 8 },
  guideTip: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', lineHeight: 1.5, marginTop: 2 },
  guideClosing: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontStyle: 'italic', color: 'var(--aj-text-mid)', lineHeight: 1.5, marginTop: 10, paddingTop: 8, borderTop: '0.5px solid rgba(29,158,117,0.25)' },

  declineBox: { marginTop: 14, padding: 12, background: 'rgba(179,38,30,0.04)', border: '0.5px solid rgba(179,38,30,0.22)', borderRadius: 6 },
  declineBody: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text)', whiteSpace: 'pre-wrap', lineHeight: 1.5, background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 6, padding: 12 },
  copyMini: { fontFamily: '"DM Sans", sans-serif', fontSize: 10.5, color: '#1a6fa8', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' },

  histHeadRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '16px 0 6px', borderTop: '0.5px solid var(--aj-border)', paddingTop: 14 },
  histHead: { fontFamily: '"Montserrat", sans-serif', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--aj-text-mid)' },
  histRow: { display: 'flex', gap: 11, padding: '9px 0', borderBottom: '0.5px solid var(--aj-border)' },
  histIcon: { fontSize: 14, width: 20, flexShrink: 0, textAlign: 'center', lineHeight: '18px' },
  histType: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, fontWeight: 600, color: 'var(--aj-text)' },
  histNotes: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', marginTop: 2, lineHeight: 1.4 },
  histMeta: { fontFamily: '"DM Mono", monospace', fontSize: 11, color: 'var(--aj-text-mid)', marginTop: 3 },
  histActions: { display: 'flex', gap: 2, flexShrink: 0, alignItems: 'flex-start' },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 12, padding: '2px 5px', borderRadius: 4 },
  muted: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', fontStyle: 'italic', padding: '4px 0' },
}
